import { useState, useCallback } from "react";
import { Type, Trash2, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Plus, Palette, Minus } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  TextOverlay,
  OverlayState,
  OVERLAY_FONTS,
  OVERLAY_COLORS,
  createTextOverlay,
  resolveOverlayAnchor,
} from "@scroll/shared-types/overlays";

interface VideoTextEditorProps {
  overlayState: OverlayState;
  onOverlayChange: (state: OverlayState) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export default function VideoTextEditor({
  overlayState,
  onOverlayChange,
  containerRef,
}: VideoTextEditorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showToolbar, setShowToolbar] = useState(false);

  const selected = overlayState.textOverlays.find((t) => t.id === selectedId) ?? null;

  const updateOverlay = useCallback(
    (id: string, patch: Partial<TextOverlay>) => {
      onOverlayChange({
        ...overlayState,
        textOverlays: overlayState.textOverlays.map((t) =>
          t.id === id ? { ...t, ...patch } : t
        ),
      });
    },
    [overlayState, onOverlayChange]
  );

  const addText = useCallback(() => {
    // Web renders overlays center-anchored; the factory records it so
    // Expo renderers (top-left legacy default) place it correctly.
    const overlay = createTextOverlay("center");
    onOverlayChange({
      ...overlayState,
      textOverlays: [...overlayState.textOverlays, overlay],
    });
    setSelectedId(overlay.id);
    setEditingId(overlay.id);
    setShowToolbar(true);
  }, [overlayState, onOverlayChange]);

  const removeOverlay = useCallback(
    (id: string) => {
      onOverlayChange({
        ...overlayState,
        textOverlays: overlayState.textOverlays.filter((t) => t.id !== id),
      });
      if (selectedId === id) {
        setSelectedId(null);
        setShowToolbar(false);
      }
    },
    [overlayState, onOverlayChange, selectedId]
  );

  // Drag handlers
  const handlePointerDown = useCallback(
    (e: React.PointerEvent, overlay: TextOverlay) => {
      e.stopPropagation();
      e.preventDefault();
      if (editingId === overlay.id) return; // don't drag while editing

      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();

      // Calculate offset from pointer to overlay center (in %)
      const pointerXPct = ((e.clientX - rect.left) / rect.width) * 100;
      const pointerYPct = ((e.clientY - rect.top) / rect.height) * 100;

      setDragging({
        id: overlay.id,
        offsetX: pointerXPct - overlay.x,
        offsetY: pointerYPct - overlay.y,
      });
      setSelectedId(overlay.id);
      setShowToolbar(true);

      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [containerRef, editingId]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100 - dragging.offsetX));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100 - dragging.offsetY));

      updateOverlay(dragging.id, { x, y });
    },
    [dragging, containerRef, updateOverlay]
  );

  const handlePointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  // Click on container background = deselect
  const handleContainerClick = useCallback(() => {
    if (!dragging) {
      setSelectedId(null);
      setEditingId(null);
      setShowToolbar(false);
    }
  }, [dragging]);

  // Double-click to edit text
  const handleDoubleClick = useCallback((e: React.MouseEvent, overlay: TextOverlay) => {
    e.stopPropagation();
    setEditingId(overlay.id);
    setSelectedId(overlay.id);
    setShowToolbar(true);
  }, []);

  return (
    <>
      {/* Overlay layer on top of video */}
      <div
        className="absolute inset-0 z-10"
        onClick={handleContainerClick}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{ cursor: dragging ? "grabbing" : "default" }}
      >
        {overlayState.textOverlays.map((overlay) => {
          const isSelected = selectedId === overlay.id;
          const isEditing = editingId === overlay.id;

          return (
            <div
              key={overlay.id}
              className="absolute select-none"
              style={{
                left: `${overlay.x}%`,
                top: `${overlay.y}%`,
                transform: `${resolveOverlayAnchor(overlay, "center") === "center" ? "translate(-50%, -50%) " : ""}rotate(${overlay.rotation}deg)`,
                opacity: overlay.opacity,
                cursor: dragging?.id === overlay.id ? "grabbing" : "grab",
                zIndex: isSelected ? 20 : 10,
              }}
              onPointerDown={(e) => handlePointerDown(e, overlay)}
              onDoubleClick={(e) => handleDoubleClick(e, overlay)}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(overlay.id);
                setShowToolbar(true);
              }}
            >
              {/* Selection border */}
              {isSelected && (
                <div className="absolute -inset-2 border-2 border-white/80 rounded pointer-events-none" />
              )}

              {isEditing ? (
                <textarea
                  autoFocus
                  value={overlay.text}
                  onChange={(e) => updateOverlay(overlay.id, { text: e.target.value })}
                  onBlur={() => setEditingId(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      setEditingId(null);
                    }
                  }}
                  className="bg-transparent border-none outline-none resize-none text-center min-w-[60px]"
                  style={{
                    color: overlay.color,
                    fontSize: `${overlay.fontSize}px`,
                    fontFamily: overlay.fontFamily,
                    fontWeight: overlay.bold ? "bold" : "normal",
                    fontStyle: overlay.italic ? "italic" : "normal",
                    textAlign: overlay.textAlign,
                    backgroundColor: overlay.backgroundColor || "transparent",
                    WebkitTextStroke: "1px rgba(0,0,0,0.3)",
                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                  }}
                  rows={overlay.text.split("\n").length}
                  cols={Math.max(10, Math.max(...overlay.text.split("\n").map((l) => l.length)))}
                />
              ) : (
                <div
                  style={{
                    color: overlay.color,
                    fontSize: `${overlay.fontSize}px`,
                    fontFamily: overlay.fontFamily,
                    fontWeight: overlay.bold ? "bold" : "normal",
                    fontStyle: overlay.italic ? "italic" : "normal",
                    textAlign: overlay.textAlign,
                    backgroundColor: overlay.backgroundColor || "transparent",
                    padding: overlay.backgroundColor ? "4px 8px" : undefined,
                    borderRadius: overlay.backgroundColor ? "4px" : undefined,
                    WebkitTextStroke: "1px rgba(0,0,0,0.3)",
                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {overlay.text}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Text button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          addText();
        }}
        className="absolute top-3 left-3 z-20 flex items-center gap-1.5 bg-black/60 hover:bg-black/80 text-white text-xs rounded-full px-3 py-1.5 transition-colors"
      >
        <Plus size={14} />
        <Type size={14} />
      </button>

      {/* Toolbar */}
      {showToolbar && selected && (
        <TextToolbar
          overlay={selected}
          onUpdate={(patch) => updateOverlay(selected.id, patch)}
          onDelete={() => removeOverlay(selected.id)}
        />
      )}
    </>
  );
}

function TextToolbar({
  overlay,
  onUpdate,
  onDelete,
}: {
  overlay: TextOverlay;
  onUpdate: (patch: Partial<TextOverlay>) => void;
  onDelete: () => void;
}) {
  const [showColors, setShowColors] = useState(false);
  const [showFonts, setShowFonts] = useState(false);

  return (
    <div className="absolute bottom-0 left-0 right-0 z-30 bg-black/90 rounded-t-xl p-3 space-y-3">
      {/* Row 1: style buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onUpdate({ bold: !overlay.bold })}
          className={`p-2 rounded ${overlay.bold ? "bg-white/20" : "hover:bg-white/10"} text-white`}
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => onUpdate({ italic: !overlay.italic })}
          className={`p-2 rounded ${overlay.italic ? "bg-white/20" : "hover:bg-white/10"} text-white`}
        >
          <Italic size={16} />
        </button>

        <div className="w-px h-6 bg-white/20" />

        <button
          type="button"
          onClick={() => onUpdate({ textAlign: "left" })}
          className={`p-2 rounded ${overlay.textAlign === "left" ? "bg-white/20" : "hover:bg-white/10"} text-white`}
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => onUpdate({ textAlign: "center" })}
          className={`p-2 rounded ${overlay.textAlign === "center" ? "bg-white/20" : "hover:bg-white/10"} text-white`}
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => onUpdate({ textAlign: "right" })}
          className={`p-2 rounded ${overlay.textAlign === "right" ? "bg-white/20" : "hover:bg-white/10"} text-white`}
        >
          <AlignRight size={16} />
        </button>

        <div className="w-px h-6 bg-white/20" />

        <button
          type="button"
          onClick={() => setShowColors(!showColors)}
          className="p-2 rounded hover:bg-white/10 text-white"
        >
          <Palette size={16} />
        </button>

        <button
          type="button"
          onClick={() => setShowFonts(!showFonts)}
          className="p-2 rounded hover:bg-white/10 text-white text-xs font-medium"
        >
          Aa
        </button>

        <div className="flex-1" />

        <button
          type="button"
          onClick={onDelete}
          className="p-2 rounded hover:bg-red-500/30 text-red-400"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Font size slider */}
      <div className="flex items-center gap-3">
        <Minus size={14} className="text-white/60" />
        <Slider
          value={[overlay.fontSize]}
          onValueChange={([v]) => onUpdate({ fontSize: v })}
          min={12}
          max={72}
          step={1}
          className="flex-1 **:data-radix-slider-track:bg-white/20 **:data-radix-slider-range:bg-[#FF6B5B] **:data-radix-slider-thumb:bg-white **:data-radix-slider-thumb:border-0"
        />
        <Plus size={14} className="text-white/60" />
        <span className="text-white/60 text-xs w-8 text-right">{overlay.fontSize}</span>
      </div>

      {/* Color picker */}
      {showColors && (
        <div className="flex items-center gap-2 flex-wrap">
          {OVERLAY_COLORS.map((color) => (
            <button
              type="button"
              key={color}
              onClick={() => onUpdate({ color })}
              className="w-7 h-7 rounded-full border-2 transition-transform"
              style={{
                backgroundColor: color,
                borderColor: overlay.color === color ? "#FF6B5B" : "rgba(255,255,255,0.2)",
                transform: overlay.color === color ? "scale(1.2)" : "scale(1)",
              }}
            />
          ))}
          {/* Background toggle */}
          <button
            type="button"
            onClick={() =>
              onUpdate({
                backgroundColor: overlay.backgroundColor ? undefined : "rgba(0,0,0,0.6)",
              })
            }
            className={`px-2 py-1 rounded text-xs text-white ${overlay.backgroundColor ? "bg-white/20" : "hover:bg-white/10"} border border-white/20`}
          >
            BG
          </button>
        </div>
      )}

      {/* Font picker */}
      {showFonts && (
        <div className="flex items-center gap-2 flex-wrap">
          {OVERLAY_FONTS.map((font) => (
            <button
              type="button"
              key={font}
              onClick={() => onUpdate({ fontFamily: font })}
              className={`px-3 py-1 rounded text-xs text-white transition-colors ${overlay.fontFamily === font ? "bg-white/20" : "hover:bg-white/10"}`}
              style={{ fontFamily: font }}
            >
              {font}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { VideoTextEditor };
export type { VideoTextEditorProps };
